import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import * as React from 'react';

export default function AnalyticsCharts({
  hasTripsData,
  hasDataPointsData,
  packages_by_hour,
  drivers_by_hour,
  collected_by_hour
}) {
  const theme = useTheme();

  // Cores do tema
  const primaryColor = theme.palette.blue.main;
  const secondaryColor = theme.palette.blue.dark;
  const successColor = theme.palette.success.main;

  // Preparar dados para o gráfico de Trips
  const tripsChartData = React.useMemo(() => {
    if (!hasTripsData) return null;

    const hours = Object.keys(packages_by_hour);
    const packagesData = Object.values(packages_by_hour);
    const driversData = Object.values(drivers_by_hour);

    return {
      xAxisData: hours,
      series: [
        {
          id: 'packages',
          label: 'Soma dos Volumes',
          data: packagesData,
          color: primaryColor,
          area: true,
        },
        {
          id: 'drivers',
          label: 'Contagem de Chegada',
          data: driversData,
          color: secondaryColor,
          area: false,
        }
      ]
    };
  }, [hasTripsData, packages_by_hour, drivers_by_hour, primaryColor, secondaryColor]);

  // Preparar dados para o gráfico de DataPoints
  const dataPointsChartData = React.useMemo(() => {
    if (!hasDataPointsData) return null;

    const hours = Object.keys(collected_by_hour);
    const collectedData = Object.values(collected_by_hour);

    // Adicionar padding conforme a versão ERB
    const paddedHours = ['', ...hours, ''];
    const paddedData = [0, ...collectedData, 0];

    return {
      xAxisData: paddedHours,
      series: [
        {
          id: 'collected',
          label: 'Contagem de Coletado',
          data: paddedData,
          color: successColor,
          area: true,
        }
      ]
    };
  }, [hasDataPointsData, collected_by_hour, successColor]);

  // Se só temos um tipo de dados, exibir em largura total
  const showSideBySide = hasTripsData && hasDataPointsData;
  const chartHeight = showSideBySide ? 350 : 400;

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Gráfico de Trips */}
        {hasTripsData && tripsChartData && (
          <Grid size={{ xs: 12, lg: showSideBySide ? 6 : 12 }}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Análise de Entregas
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Volumes entregues e chegada de veículos por hora
                </Typography>
                <Box sx={{ height: chartHeight, width: '100%' }}>
                  <LineChart
                    colors={[primaryColor, secondaryColor]}
                    series={tripsChartData.series}
                    height={chartHeight}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: tripsChartData.xAxisData,
                        label: 'Hora',
                      },
                    ]}
                    yAxis={[
                      {
                        label: 'Volumes / Veículos',
                      },
                    ]}
                    grid={{ horizontal: true, vertical: true }}
                    sx={{
                      '& .MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '& .MuiMarkElement-root': {
                        stroke: '#fff',
                        strokeWidth: 2,
                        r: 4,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Gráfico de Data Points */}
        {hasDataPointsData && dataPointsChartData && (
          <Grid size={{ xs: 12, lg: showSideBySide ? 6 : 12 }}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Análise de Coleta
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Volumes coletados por hora
                </Typography>
                <Box sx={{ height: chartHeight, width: '100%' }}>
                  <LineChart
                    series={dataPointsChartData.series}
                    height={chartHeight}
                    xAxis={[
                      {
                        scaleType: 'point',
                        data: dataPointsChartData.xAxisData,
                        label: 'Hora',
                      },
                    ]}
                    yAxis={[
                      {
                        label: 'Volumes Coletados',
                      },
                    ]}
                    grid={{ horizontal: true, vertical: true }}
                    sx={{
                      '& .MuiAreaElement-root': {
                        fillOpacity: 0.4,
                      },
                      '& .MuiLineElement-root': {
                        strokeWidth: 2,
                      },
                      '& .MuiMarkElement-root': {
                        stroke: '#fff',
                        strokeWidth: 2,
                        r: 4,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
} 